import type { ReactNode } from 'react';
import LocaleLink from '@/components/LocaleLink';
import { isAppUrl } from '@/config/appLinks';

export interface MaybeLinkProps {
  to: string;
  live?: boolean;
  external?: boolean;
  opensModal?: boolean;
  onModalClick?: () => void;
  className?: string;
  children: ReactNode;
}

/**
 * Renders a real link when `live`, or a greyed, non-focusable, non-clickable
 * placeholder when not. Used by TopNav + Footer so links to not-yet-built
 * pages show in the sitemap without 404ing. The placeholder is an <a> with no
 * href so it inherits the container's link typography, then `.mkt-link-disabled`
 * greys it and blocks pointer events.
 */
export default function MaybeLink({
  to,
  live,
  external,
  opensModal,
  onModalClick,
  className,
  children,
}: MaybeLinkProps) {
  if (!live) {
    return (
      <a
        className={['mkt-link-disabled', className].filter(Boolean).join(' ')}
        aria-disabled="true"
      >
        {children}
      </a>
    );
  }
  if (opensModal) {
    return (
      <button type="button" className={className} onClick={onModalClick}>
        {children}
      </button>
    );
  }
  if (to.startsWith('mailto:')) {
    /* Email links open in the mail client, not a new browser tab: no
     * target/rel (owner rule 2026-07-12, mirrors the legal-page policy). */
    return (
      <a href={to} className={className}>
        {children}
      </a>
    );
  }
  if (external) {
    /* App links are cross-origin but first-party, so they stay in the current
     * tab (see isAppUrl in src/config/appLinks.ts). rel is still set: it costs
     * nothing and keeps the link safe if it ever does gain a target. */
    if (isAppUrl(to)) {
      return (
        <a href={to} className={className} rel="noopener noreferrer">
          {children}
        </a>
      );
    }
    return (
      <a href={to} className={className} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }
  return (
    <LocaleLink to={to} className={className}>
      {children}
    </LocaleLink>
  );
}
